<template>
    <Head :title="__('Edit')"/>

    <BreezeDashboardLayout>
        <template #header>
            <div class="font-semibold text-xl text-gray-800 leading-tight">
                {{ __('Edit') }}
            </div>
        </template>

        <notification v-if="success" color="success">
            {{ success }}
        </notification>

        <notification v-if="Object.keys(errors).length" color="danger">
            <ul class="list-inside">
                <li v-for="error in errors">{{ error }}</li>
            </ul>
        </notification>

        <the-modal-box v-model="isConfirmModalActive" :title="__('Please confirm action')" has-cancel
                       @confirm="confirmDeletion">
            <p>{{ __('Are you sure you want to delete this resource?') }}</p>
        </the-modal-box>

        <the-modal-box v-model="isConfirmChangeSiteModalActive" :title="__('Please confirm action')" has-cancel
                       @confirm="confirmChangeSite" @cancel="cancelChangeSite">
            <p>{{ __('Are you sure you want to change the site? It will remove selected nomenclature.') }}</p>
        </the-modal-box>

        <the-modal-box v-model="isGenerationModalActive" :title="__('Please wait')" :has-control="false">
            <p>{{ __('Your price is generated, please do not refresh the page') }}</p>
            <p class="flex justify-center pt-2">
                <!-- By Sam Herbert (@sherb), for everyone. More @ http://goo.gl/7AJzbL -->
                <svg xmlns="http://www.w3.org/2000/svg" width="60" height="15" viewBox="0 0 120 30" fill="#3b82f6">
                    <circle cx="15" cy="15" r="15">
                        <animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15"
                                 calcMode="linear" repeatCount="indefinite"/>
                        <animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1"
                                 calcMode="linear" repeatCount="indefinite"/>
                    </circle>
                    <circle cx="60" cy="15" r="9" fill-opacity="0.3">
                        <animate attributeName="r" from="9" to="9" begin="0s" dur="0.8s" values="9;15;9"
                                 calcMode="linear" repeatCount="indefinite"/>
                        <animate attributeName="fill-opacity" from="0.5" to="0.5" begin="0s" dur="0.8s" values=".5;1;.5"
                                 calcMode="linear" repeatCount="indefinite"/>
                    </circle>
                    <circle cx="105" cy="15" r="15">
                        <animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15"
                                 calcMode="linear" repeatCount="indefinite"/>
                        <animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1"
                                 calcMode="linear" repeatCount="indefinite"/>
                    </circle>
                </svg>
            </p>
        </the-modal-box>

        <card-component :title="__('Price Settings')" :icon="mdiListStatus" @submit.prevent="submit" form>
            <div class="md:grid md:grid-cols-2 md:gap-2">
                <div>
                    <field :label="__('Author')" :error="errors.user_id ?? ''">
                        <control :disabled="!isPMUser" :icon="mdiBookAccount" :options="userOptions" v-model="form.user_id"
                                 type="select-search"
                                 placeholder="Автор"
                                 :required="true"/>
                    </field>
                    <field :label="__('Site')" :error="errors.site ?? ''">
                        <control :icon="mdiLink" :placeholder="__('Site')" v-model="formSite" :options="siteOptions"
                                 :required="true"/>
                    </field>
                    <field :label="__('Client login')" :error="errors.name ?? ''">
                        <control :icon="mdiAccount" :placeholder="__('Client login')" v-model="form.name"
                                 :required="true" :autocompleteOptions="nameOptions" autocompleteLabel="username"
                                 :isLoading="isLoading" @autocompleteOptionSelect="nameAutocompleteOptionSelect"
                                 @keyup="asyncFind"/>
                    </field>
                    <field :label="__('Client Site')" :error="errors.client_site ?? ''">
                        <control :icon="mdiOpenInNew" :placeholder="__('Client Site')" v-model="form.client_site"/>
                    </field>
                    <field :label="__('Email')" :error="errors.email ?? ''">
                        <control :icon="mdiEmail" :placeholder="__('Email')" v-model="form.email"
                                 :required="true"/>
                    </field>
                    <field :label="__('Format')" :error="errors.file_format ?? ''">
                        <control :icon="mdiPuzzleOutline" :options="fileFormatOptions" v-model="form.file_format"
                                 :required="true"/>
                    </field>
                    <field class="max-w-sm" :label="__('Update interval')"
                           :help="__('0-8 times a day, starting at 6 o\'clock in the morning')"
                           :error="errors.update_interval ?? ''">
                        <control :icon="mdiAvTimer" :options="updateIntervalOptions" v-model="form.update_interval"
                                 :required="true"/>
                    </field>
                </div>
                <div>
                    <field class="mt-11">
                        <check-radio-picker
                            class="-ml-7"
                            name="sample-switch"
                            type="switch"
                            v-model="form.usd_currency"
                            :options="{ usd_currency: __('USD Currency') }"
                        ></check-radio-picker>
                    </field>
                    <field>
                        <check-radio-picker
                            class="-ml-7"
                            name="only_available_products"
                            type="switch"
                            v-model="form.only_available_products"
                            :options="{ only_available_products: __('Only availability in stock') }"
                            :readonly="availableProductsReadonly"
                        />
                    </field>
                    <field>
                        <check-radio-picker
                            class="-ml-7"
                            name="exchange_changes"
                            type="switch"
                            v-model="form.exchange_changes"
                            :options="{ exchange_changes: __('Update when the exchange rate changes') }"
                        ></check-radio-picker>
                    </field>
                    <catalog-selector v-model="form.nomenclatures" :site="formSite" class="mt-11"/>
                    <stores-selector v-model="form.stores" :site="formSite"/>
                    <field :label="__('Download link')">
                        <span class="text-blue-700 break-words">
                            <a :href="route('price-configs.download', priceConfig.hash)" target="_blank">
                                {{ route('price-configs.download', priceConfig.hash) }}
                            </a>
                        </span>
                    </field>
                    <field v-if="form.file_format.id !== 'xlsx'" :label="__('Direct link')">
                        <span class="text-blue-700 break-words">
                            <a :href="route('price-configs.show', priceConfig.hash)" target="_blank">
                                {{ route('price-configs.show', priceConfig.hash) }}
                            </a>
                        </span>
                    </field>
                </div>
            </div>
            <field class="max-w-sm" :label="__('Price language')" :error="errors.price_language ?? ''">
                <control :icon="mdiTranslate" :options="languageOptions" v-model="form.price_language"
                         :required="true"/>
            </field>
        </card-component>

        <div class="md:grid md:grid-cols-3 md:gap-2">
            <card-component :title="__('Price Fields Settings')" :icon="mdiViewColumnOutline">
                <div class="xl:grid xl:grid-cols-2 xl:gap-2 mt-4">
                    <field :error="errors.price_fields ?? ''">
                        <check-radio-picker class="checkbox-block" type="checkbox mr-0"
                                            name="sample-checkbox"
                                            v-model="form.price_fields"
                                            :options="priceFieldsOptions"
                        />
                    </field>

                    <card-component  class="col-span-1">
                        <field :error="errors.rozetka_description ?? ''" class="mb-2 mt-3">
                            <label class="checkbox">
                                <input
                                    type="checkbox"
                                    name="rozetka_description"
                                    v-model="form.rozetka_description"
                                    :disabled="!isPMUser"
                                >
                                <span class="check"></span>
                                <span class="control-label pr-3 pl-0">{{ __('Price rozetka description') }}</span>
                            </label>
                        </field>

                        <field class="max-w-sm mb-2" :label="__('Price additional image')"
                               :error="errors.additional_image ?? ''">
                            <control :icon="mdiImage" :options="additionalImageOptions" v-model="form.additional_image"
                                     :disabled="!isPMUser"
                                     :required="true"/>
                        </field>

                        <field :error="errors.use_special_price ?? ''" class="mb-2 mt-3">
                            <label class="checkbox">
                                <input
                                    type="checkbox"
                                    name="use_special_price"
                                    v-model="form.use_special_price"
                                    :disabled="!isPMUser"
                                >
                                <span class="check"></span>
                                <span class="control-label pr-3 pl-0">{{ __('Use special price') }}</span>
                            </label>
                        </field>
                    </card-component>
                </div>
            </card-component>
            <card-component :title="__('Price Price Settings')" :icon="mdiCurrencyUsd" class="col-span-2">
                <field :error="errors.price_prices ?? ''">
                    <prices-picker class="checkbox-grid" type="checkbox mr-0"
                                   name="sample-checkbox"
                                   v-model="form.price_prices"
                                   :options="pricePricesOptions"
                                   :priority="form.price_priority"
                    />
                </field>
            </card-component>
        </div>

        <jb-buttons class="md:justify-end ml-3 md:ml-0" mb="">
            <jb-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing" type="button"
                       color="success" :label="__('Save :resource', {resource: __('The Price').toLowerCase()})"
                       @click.prevent="submit"/>
            <jb-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing" type="button"
                       @click="downloadPrice"
                       target="_blank"
                       color="info" :label="__('Download :resource', {resource: __('The Price').toLowerCase()})"/>
            <jb-button type="button" color="warning" outline :label="__('Cancel')"
                       @click.prevent="cancel"/>
            <jb-button type="button" color="danger" outline
                       :label="__('Delete :resource', {resource: __('The Price').toLowerCase()})"
                       @click.prevent="isConfirmModalActive = true"/>
        </jb-buttons>

    </BreezeDashboardLayout>

    <iframe id="download" style="display:none;"></iframe>
</template>

<style>
</style>

<script>
import BreezeDashboardLayout from '@/Layouts/Dashboard'
import CardComponent from '@/Components/Dashboard/CardComponent'
import TheModalBox from '@/Components/Dashboard/TheModalBox'
import Notification from '@/Components/Dashboard/Notification'
import {Head, usePage} from '@inertiajs/inertia-vue3';
import {ref, reactive, computed, defineProps, toRefs, onMounted, getCurrentInstance} from 'vue'
import {
    mdiBookAccount,
    mdiListStatus,
    mdiAccount,
    mdiEmail,
    mdiLink,
    mdiOpenInNew,
    mdiPuzzleOutline,
    mdiAvTimer,
    mdiTranslate,
    mdiPlusBox,
    mdiCloseBoxMultiple,
    mdiCurrencyUsd,
    mdiViewColumnOutline,
    mdiImage,
} from '@mdi/js'
import Field from '@/Components/Dashboard/Field'
import Control from '@/Components/Dashboard/Control'
import CheckRadioPicker from '@/Components/Dashboard/CheckRadioPicker'
import PricesPicker from '@/Components/Dashboard/PricesPicker'
import JbButton from '@/Components/Dashboard/JbButton'
import JbButtons from '@/Components/Dashboard/JbButtons'
import CatalogSelector from "@/Components/Dashboard/CatalogSelector"
import StoresSelector from "@/Components/Dashboard/StoresSelector"

export default {
    components: {
        CatalogSelector,
        StoresSelector,
        BreezeDashboardLayout,
        Head,
        TheModalBox,
        Notification,
        CardComponent,
        Field,
        Control,
        CheckRadioPicker,
        PricesPicker,
        JbButton,
        JbButtons
    },
    props: {
        success: String,
        errors: Object,
        priceConfig: Object,
        priceFieldsOptions: Object,
        pricePricesOptions: Object,
        userOptions: Array,
        fileFormatOptions: Array,
        updateIntervalOptions: Array,
        languageOptions: Array,
        siteOptions: Array,
        additionalImageOptions: Array,
        isPMUser: Boolean,
    },
    watch: {
        formSite(newVal, oldVal) {
            if (this.formSiteOldValue === null) {
                if (this.form.nomenclatures.length > 0 || this.form.name !== '' || this.form.email !== '' || this.form.stores.length > 0) {
                    this.isConfirmChangeSiteModalActive = true
                    this.formSiteOldValue = oldVal
                } else {
                    this.form.site = this.formSite
                }
            } else {
                this.formSiteOldValue = null
            }
        },
        "form.nomenclatures": {
            handler() {
                this.autoSave()
            },
            deep: true
        },
        "form.stores": {
            handler(newVAl, oldVal) {
                this.availableProductsToggle(newVAl.length > 0)
                this.autoSave()
            },
            deep: true
        },
        "form.user_id"() {
            this.autoSave()
        },
        "form.file_format"() {
            this.autoSave()
        },
        "form.update_interval"() {
            this.autoSave()
        },
        "form.price_language"() {
            this.autoSave()
        },
        "form.usd_currency"() {
            this.autoSave()
        },
        "form.exchange_changes"() {
            this.autoSave()
        },
        "form.price_fields"() {
            this.autoSave()
        },
        "form.price_prices"(newVal) {
            if (newVal.length > 0) {
                this.autoSave()
            }
        },
        "form.rozetka_description"() {
            this.autoSave()
        },
        "form.additional_image"() {
            this.autoSave()
        },
        "form.use_special_price"() {
            this.autoSave()
        }
    },
    data(props) {
        return {
            form: this.$inertia.form({
                user_id: props.userOptions.find(item => item.id === props.priceConfig.user_id),
                name: props.priceConfig.name,
                site: props.siteOptions.find(item => item.id === props.priceConfig.site_id),
                client_site: props.priceConfig.client_site,
                nomenclatures: props.priceConfig.nomenclatures.map(item => {
                    const data = {
                        id: item.api_source_id,
                        catalog_id: item.catalog_id,
                        subject_id: item.subject_id ?? null,
                        name: item.name,
                        type: item.type
                    }

                    if (data.type === 'catalog') {
                        data.id = data.catalog_id
                    }

                    return data
                }),
                stores: props.priceConfig.stores.map(item => {
                    return {
                        id: item.api_source_id,
                        name: item.name,
                        type: item.type
                    }
                }),
                email: props.priceConfig.email,
                file_format: props.fileFormatOptions.find(item => item.id === props.priceConfig.file_format),
                update_interval: props.updateIntervalOptions.find(item => item.id === props.priceConfig.update_interval),
                price_language: props.languageOptions.find(item => item.id === props.priceConfig.price_language),
                usd_currency: props.priceConfig.usd_currency ? ['usd_currency'] : [],
                exchange_changes: props.priceConfig.exchange_changes ? ['exchange_changes'] : [],
                price_fields: props.priceConfig.price_fields.map(item => item.api_source_id),
                rozetka_description: props.priceConfig.rozetka_description == 1,
                additional_image: props.additionalImageOptions.find(item => item.id === props.priceConfig.additional_image),
                use_special_price: props.priceConfig.use_special_price == 1,
                user_price_type_id: props.priceConfig.user_price_type_id,
                site_user_id: props.priceConfig.site_user_id,
                price_prices: props.priceConfig.price_prices.map(item => item.api_source_id),
                only_available_products: props.priceConfig.only_available_products == 1,
                price_priority: props.priceConfig.price_priority,
                dot_not_redirect: false
            }),
            formSite: props.siteOptions.find(item => item.id === props.priceConfig.site_id),
            selectedUser: props.userOptions.find(item => item.id === props.priceConfig.user_id),
            formSiteOldValue: null,
            isLoading: false,
            nameOptions: [],
            availableProductsReadonly: false,
        }
    },
    mounted() {
        document.getElementById('download').removeAttribute('src');
    },
    setup(props) {
        const isConfirmModalActive = ref(false)
        const isGenerationModalActive = ref(false)
        const isConfirmChangeSiteModalActive = ref(false)
        const user = computed(() => usePage().props.value.auth.user)

        onMounted(() => {
            if (props.priceConfig.stores.length > 0) {
                getCurrentInstance().data.form.only_available_products = true
                getCurrentInstance().data.availableProductsReadonly = true
            }
        })

        return {
            user,
            mdiBookAccount,
            mdiListStatus,
            mdiAccount,
            mdiLink,
            mdiOpenInNew,
            mdiEmail,
            mdiPuzzleOutline,
            mdiAvTimer,
            mdiTranslate,
            mdiPlusBox,
            mdiCloseBoxMultiple,
            mdiCurrencyUsd,
            mdiViewColumnOutline,
            mdiImage,
            isConfirmModalActive,
            isConfirmChangeSiteModalActive,
            isGenerationModalActive
        }
    },
    beforeMount() {
        document.body.addEventListener('click', this.nameOptionsOutClick);
    },
    methods: {
        downloadPrice() {
            const self = this
            let needModal = true

            setTimeout(() => {
                if (needModal) {
                    self.isGenerationModalActive = true
                }
            }, 500)

            axios.post(this.route('api.v1.price-configs.async-download'), {
                'hash': this.priceConfig.hash
            }).then(response => {
                needModal = false
                setTimeout(() => {
                    self.isGenerationModalActive = false
                }, 1000)
                document.getElementById('download').src = self.route('price-configs.download', self.priceConfig.hash);
            }).then(error => {
                self.isGenerationModalActive = false
            }).catch(error => {
                if (typeof error.message !== "undefined") {
                    alert(error.message)
                }
                self.isGenerationModalActive = false
                console.log(error)
            });
        },
        availableProductsToggle(setAndBlock = true) {
            if (setAndBlock) {
                this.form.only_available_products = true
                this.availableProductsReadonly = true
            } else {
                this.form.only_available_products = false
                this.availableProductsReadonly = false
            }
        },
        nameOptionsOutClick(e) {
            if (typeof this.nameOptions !== "undefined" && this.nameOptions.length > 0) {
                const el = e.target
                if (el.classList.contains('autocomplete-options') === false ||
                    el.classList.contains('autocomplete-options__option') === false) {
                    this.nameOptions = []
                    this.form.site_user_id = null
                }
            }
        },
        nameAutocompleteOptionSelect(autocompleteOption) {
            this.nameOptions = []
            this.form.email = autocompleteOption.email
            const language_code = this.getLanguageCodeById(autocompleteOption.language_id)
            this.form.price_language = this.languageOptions.find(item => item.id === language_code)
            this.form.price_prices = [autocompleteOption.price_type_id]
            this.form.user_price_type_id = autocompleteOption.price_type_id
            this.form.site_user_id = autocompleteOption.id
            const priceOption = this.pricePricesOptions.find(item => item.api_source_id == autocompleteOption.price_type_id)
            if (priceOption) {
                this.form.price_priority = priceOption.order;
            } else {
                this.form.price_priority = 0;
            }
        },
        getLanguageCodeById(id) {
            let code = 'en'
            if (id == 5) {
                code = 'ru'
            } else if (id == 6) {
                code = 'uk'
            } else if (id == 3) {
                code = 'es'
            }
            return code
        },
        asyncFind(event) {
            const self = this

            if (event.target.value.length < 3) {
                this.nameOptions = []
                return
            }

            const query = event.target.value

            if (this.isLoading === true) {
                return
            }

            self.isLoading = true
            axios.post(this.route('api.v1.site-users.index'), {
                'siteId': this.form.site.api_source_id,
                'search': query
            }).then(response => {
                self.isLoading = false
                self.nameOptions = response.data
                let isSameName = false;
                self.nameOptions.forEach((item) => {
                    if (query == item.username && this.form.site_user_id == item.id) {
                        isSameName = true
                    }
                });

                if (!isSameName) {
                    this.form.site_user_id = null
                }
            }).then(error => {
                self.isLoading = false
            });
        },
        autoSave() {
            if (this.form.name === '' || this.form.email === '') {
                return
            }

            const self = this
            self.form.dot_not_redirect = true
            self.form.processing = true
            self.$emit('errors', {})
            axios.put(this.route('price-configs.update', this.priceConfig.id), this.form).then(res => {
                self.clearAfterAutoSave()
            }).catch(error => {
                const data = error.response.data
                if (typeof data.errors !== "undefined") {
                    self.$emit('errors', data.errors)
                }
                self.clearAfterAutoSave()
            })
        },
        clearAfterAutoSave() {
            const self = this
            setTimeout(() => {
                self.form.dot_not_redirect = false
                self.form.processing = false
            }, 500)
        },
        submit() {
            this.form.put(this.route('price-configs.update', this.priceConfig.id))
        },
        cancel() {
            this.$inertia.visit(this.route('price-configs.index'))
        },
        share() {
            if (navigator.canShare && navigator.canShare({files: filesArray})) {
                navigator.share({
                    files: filesArray,
                    title: 'Pictures',
                    text: 'Our Pictures.',
                })
                    .then(() => console.log('Share was successful.'))
                    .catch((error) => console.log('Sharing failed', error));
            } else {
                console.log(`Your system doesn't support sharing files.`);
            }
        },
        confirmDeletion() {
            this.$inertia.form().delete(this.route('price-configs.destroy', this.priceConfig.id))
        },
        cancelChangeSite() {
            this.formSite = this.formSiteOldValue;
        },
        confirmChangeSite() {
            this.formSiteOldValue = null
            this.form.site = this.formSite
            this.form.nomenclatures = []
            this.form.stores = []
            this.form.name = ''
            this.form.email = ''
            this.form.price_priority = 0
            this.form.price_prices = []
            this.form.user_price_type_id = 0
            this.form.site_user_id = null
        }
    }
}
</script>
